import React, { useEffect, useRef, useState } from 'react';

/**
 * TurnstileWidget — Wrapper React pour Cloudflare Turnstile
 *
 * Composant réutilisable qui :
 *   1. Charge le script Cloudflare https://challenges.cloudflare.com/turnstile/v0/api.js
 *      une seule fois (idempotent — détecte si déjà chargé)
 *   2. Rend un <div> qui sera transformé en widget par Cloudflare
 *   3. Expose onSuccess(token) / onError() / onExpire() au parent
 *
 * Props :
 *   - siteKey       (string, requise) — Site Key publique Cloudflare
 *                   (VITE_TURNSTILE_SITE_KEY côté .env frontend)
 *   - onSuccess     (fn, requise)     — callback(token) quand vérification OK
 *   - onError       (fn, optionnelle) — Cloudflare détecte un problème
 *   - onExpire      (fn, optionnelle) — token expiré (~5 min après émission)
 *   - theme         (string)          — 'dark' (défaut, matche la charte ChasseHunt)
 *                                       ou 'light' / 'auto'
 *
 * Mode du widget : « managed » (défaut Cloudflare) — invisible si trafic propre,
 * défi visible si suspicion. Configuré au level du widget dans le dashboard
 * Cloudflare, pas dans le code.
 *
 * EN DEV LOCAL sans siteKey configurée : le composant affiche un placeholder
 * d'avertissement visible pour signaler le problème à l'utilisateur.
 *
 * Clés de test Cloudflare (always passes) :
 *   Site Key : 1x00000000000000000000AA
 * Doc : https://developers.cloudflare.com/turnstile/troubleshooting/testing/
 */

const CLOUDFLARE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Mémoïse la promesse de chargement du script pour éviter qu'un montage
// simultané de plusieurs TurnstileWidget ne crée plusieurs <script>.
let scriptLoadingPromise = null;

function chargerScriptCloudflare() {
  if (scriptLoadingPromise) return scriptLoadingPromise;

  // Si window.turnstile existe déjà (script déjà chargé hors React)
  if (typeof window !== 'undefined' && window.turnstile) {
    scriptLoadingPromise = Promise.resolve();
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // Si une balise <script> Turnstile existe déjà dans le DOM
    const existant = document.querySelector(`script[src="${CLOUDFLARE_SCRIPT_URL}"]`);
    if (existant) {
      existant.addEventListener('load', () => resolve());
      existant.addEventListener('error', () =>
        reject(new Error('Échec chargement Cloudflare Turnstile'))
      );
      return;
    }

    const s = document.createElement('script');
    s.src = CLOUDFLARE_SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptLoadingPromise = null; // permet une nouvelle tentative
      reject(new Error('Échec chargement Cloudflare Turnstile'));
    };
    document.head.appendChild(s);
  });

  return scriptLoadingPromise;
}

export default function TurnstileWidget({ siteKey, onSuccess, onError, onExpire, theme = 'dark' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [erreurChargement, setErreurChargement] = useState(false);

  useEffect(() => {
    if (!siteKey) return; // placeholder dev affiché plus bas

    let cancelled = false;

    chargerScriptCloudflare()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;

        // window.turnstile.render() retourne un widgetId qu'on garde pour
        // pouvoir reset/remove plus tard (notamment au unmount).
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => {
            if (!cancelled) onSuccess?.(token);
          },
          'error-callback': () => {
            if (!cancelled) onError?.();
          },
          'expired-callback': () => {
            if (!cancelled) onExpire?.();
          },
        });
      })
      .catch((err) => {
        console.error('[TurnstileWidget]', err);
        if (!cancelled) {
          setErreurChargement(true);
          onError?.();
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* idempotent silencieux */
        }
        widgetIdRef.current = null;
      }
    };
    // Note : les callbacks (onSuccess/onError/onExpire) ne sont volontairement
    // PAS dans deps — ils sont appelés via closure stable et ré-instancier le
    // widget à chaque render parent provoquerait un flash visuel.
  }, [siteKey, theme]);

  // ── Placeholder dev sans siteKey ─────────────────────────────────────
  if (!siteKey) {
    return (
      <div
        style={{
          padding: '0.85rem 1rem',
          background: 'rgba(201,162,39,0.1)',
          border: '1px dashed rgba(201,162,39,0.4)',
          borderRadius: '6px',
          color: 'rgba(201,162,39,0.85)',
          fontSize: '0.82rem',
          lineHeight: 1.5,
        }}
      >
        ⚠ <strong>VITE_TURNSTILE_SITE_KEY</strong> non configurée — le widget Cloudflare ne sera pas
        affiché. Vérification anti-bot désactivée.
      </div>
    );
  }

  // ── Erreur de chargement du script Cloudflare ────────────────────────
  if (erreurChargement) {
    return (
      <div
        style={{
          padding: '0.85rem 1rem',
          background: 'rgba(200,50,50,0.12)',
          border: '1px solid rgba(200,50,50,0.35)',
          borderRadius: '6px',
          color: 'var(--couleur-texte-erreur)',
          fontSize: '0.84rem',
        }}
      >
        Impossible de charger la vérification anti-bot. Vérifiez votre connexion.
      </div>
    );
  }

  // Cloudflare injecte son widget dans cette div
  return <div ref={containerRef} />;
}
